import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import InputEditForm from '@aurora/shared-client/components/form/InputEditForm/InputEditForm';
import NodeSelectorDropdown from '@aurora/shared-client/components/nodepicker/NodeSelectorDropdown/NodeSelectorDropdown';
import type { NodePickerNode } from '@aurora/shared-client/components/nodepicker/types';
import FormBuilder from '@aurora/shared-client/helpers/form/FormBuilder/FormBuilder';
import type { SearchResultsPageAndParams } from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import type { CoreNode, Form } from '@aurora/shared-generated/types/graphql-schema-types';
import { ConversationStyle } from '@aurora/shared-generated/types/graphql-schema-types';
import type {
  ContextNodeFragment,
  ContextUserFragment
} from '@aurora/shared-generated/types/graphql-types';
import { NodeType } from '@aurora/shared-types/nodes/enums';
import {
  EndUserComponent,
  EndUserPages,
  EndUserQueryParams
} from '@aurora/shared-types/pages/enums';
import React, { useCallback, useContext, useState } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type { OverflowSetItem } from '../../../common/OverflowSet/OverflowSet';
import OverflowSet from '../../../common/OverflowSet/OverflowSet';
import useTranslation from '../../../useTranslation';
import { LastUpdateFilterMenuItem, RepliesFilterMenuItem } from '../../enums';
import MessageSearchTagsFilter from '../SearchMessageTagsFilter/MessageSearchTagsFilter';
import SearchAppliedFilters from '../SearchAppliedFilters/SearchAppliedFilters';
import SearchFilterDropdown from '../SearchFilterDropDown/SearchFilterDropDown';
import formSchema from './SearchWidgetFiltersForm.form.json';
import localStyles from './SearchWidgetFilters.module.pcss';
import SearchUserFilter from '../SearchUserFilter/SearchUserFilter';
import { getConversationStyle } from '@aurora/shared-client/helpers/nodes/NodeHelper';

interface Props {
  /**
   * Class name(s) to apply to the component element.
   */
  className?: string;
  /**
   * The node which search should be scoped to. When not provided, search is scoped to community.
   */
  contextNode?: ContextNodeFragment;
  /**
   * The author(user) data, selected from author filter
   */
  author?: ContextUserFragment;
  /**
   * Callback function when an item in a menu is selected.
   *
   * @callback
   */
  onChange?: () => void;
}

interface FormData {
  /**
   * Whether results from replies have to be excluded.
   */
  searchOnlyInTopics: boolean;
}

/**
 * Renders filters for search widget.
 *
 * @author Amit Agrawal, Akash Goenka
 */
const SearchWidgetFilters = ({
  className,
  contextNode,
  onChange,
  author
}: Props): React.ReactElement => {
  const { community } = useContext(AppContext);
  const communityWithDepth = { ...community, depth: 0 };
  const cx = useClassNameMapper(localStyles);
  const [selectedNode, setSelectedNode] = useState<NodePickerNode | CoreNode>(
    contextNode ?? communityWithDepth
  );
  const i18n = useTranslation(EndUserComponent.SEARCH_WIDGET_FILTERS);
  const { formatMessage, loading: textLoading } = i18n;
  const { router } = useEndUserRoutes();
  const overflowedItems: OverflowSetItem[] = [];
  const tenant = useContext(TenantContext);
  const { ideasEnabled: useIdeas, occasionsEnabled: useOccasions } = tenant.publicConfig;

  const lastUpdateFilter = router.getUnwrappedQueryParam(
    EndUserQueryParams.SEARCH_FILTER_BY_LAST_UPDATE,
    ''
  );
  const contentTypeFilter = router.getUnwrappedQueryParam(
    EndUserQueryParams.SEARCH_FILTER_BY_CONTENT_TYPE,
    ''
  );
  const repliesFilter = router.getUnwrappedQueryParam(
    EndUserQueryParams.SEARCH_FILTER_BY_REPLIES,
    ''
  );

  const searchOnlyInTopicsFilter = router.getUnwrappedQueryParam(
    EndUserQueryParams.SEARCH_ONLY_IN_TOPICS
  );

  const locationFilter = router.getUnwrappedQueryParam(
    EndUserQueryParams.SEARCH_FILTER_BY_LOCATION
  );

  /**
   * Called when the form is submitted
   *
   * @param formData Data when the form is submitted.
   */
  const onSubmit = useCallback(
    async (formData: FormData): Promise<void> => {
      const { searchOnlyInTopics } = formData;
      if (searchOnlyInTopics) {
        onChange();
        await router.replaceRoute<SearchResultsPageAndParams>(
          EndUserPages.SearchResultsPage,
          null,
          {
            ...router.query,
            [EndUserQueryParams.SEARCH_ONLY_IN_TOPICS]: 'true'
          },
          { shallow: true }
        );
      } else {
        await router.removeQueryParam([EndUserQueryParams.SEARCH_ONLY_IN_TOPICS]);
      }
    },
    [onChange, router]
  );

  /**
   * Callback function for node change in the NodeSelectorDropdown
   */
  const onNodeChange = useCallback(
    async (node: CoreNode) => {
      if (community.id !== node.id) {
        onChange();
        setSelectedNode(node);
        await router.replaceRoute<SearchResultsPageAndParams>(
          EndUserPages.SearchResultsPage,
          null,
          {
            ...router.query,
            [EndUserQueryParams.SEARCH_FILTER_BY_LOCATION]: node.id
          },
          { shallow: true }
        );
      }
    },
    [community, onChange, router]
  );

  if (textLoading) {
    return null;
  }

  /**
   * Render search only in topics check field
   */
  const SearchOnlyInTopicsFilter: React.FC<React.PropsWithChildren<unknown>> = () => {
    const fieldSpecs = new FormBuilder<FormData>(
      'SearchWidgetFiltersForm',
      i18n,
      {
        schema: formSchema as Form,
        cx
      },
      {
        formClassName: cx('lia-form')
      }
    )
      .addCheckField({
        name: 'searchOnlyInTopics',
        defaultValue: searchOnlyInTopicsFilter === 'true',
        className: cx('d-flex align-items-center')
      })
      .build();

    return (
      <>
        <h5 className={cx('lia-more-option-text')}>{formatMessage('moreOptions')}</h5>
        <InputEditForm<FormData>
          formSpec={fieldSpecs}
          onSubmit={onSubmit}
          submitOnChange={{
            watchFields: ['searchOnlyInTopics']
          }}
        />
      </>
    );
  };

  const RepliesFilter: React.FC<React.PropsWithChildren<unknown>> = () => {
    return (
      <SearchFilterDropdown
        filterName="replies"
        filterMenuItems={Object.values(RepliesFilterMenuItem)}
        activeFilterMenuItem={repliesFilter}
        onChange={onChange}
        routerParam={EndUserQueryParams.SEARCH_FILTER_BY_REPLIES}
        contextNode={contextNode}
        author={author}
        useCounts
      />
    );
  };

  const LastUpdateFilter: React.FC<React.PropsWithChildren<unknown>> = () => {
    return (
      <SearchFilterDropdown
        filterName="lastUpdate"
        filterMenuItems={Object.values(LastUpdateFilterMenuItem)}
        activeFilterMenuItem={lastUpdateFilter}
        onChange={onChange}
        routerParam={EndUserQueryParams.SEARCH_FILTER_BY_LAST_UPDATE}
        contextNode={contextNode}
        author={author}
        useCounts
      />
    );
  };

  const ContentTypeFilter: React.FC<React.PropsWithChildren<unknown>> = () => {
    const menuItems = Object.values(ConversationStyle).filter(
      item =>
        item === ConversationStyle.Forum ||
        item === ConversationStyle.Blog ||
        item === ConversationStyle.Tkb ||
        (useIdeas && item === ConversationStyle.Idea) ||
        (useOccasions && item === ConversationStyle.Occasion)
    );
    return (
      <SearchFilterDropdown
        filterName="contentType"
        filterMenuItems={menuItems}
        activeFilterMenuItem={contentTypeFilter}
        onChange={onChange}
        routerParam={EndUserQueryParams.SEARCH_FILTER_BY_CONTENT_TYPE}
        contextNode={contextNode}
        author={author}
        useCounts
        menuItemsToDisable={
          contextNode?.nodeType === NodeType.BOARD
            ? Object.values(ConversationStyle).filter(
                item => item !== getConversationStyle(contextNode)
              )
            : []
        }
      />
    );
  };

  const filtersItems: OverflowSetItem[] = [
    {
      key: 'lastUpdateFilter',
      onRender: <LastUpdateFilter />
    },
    {
      key: 'repliesFilter',
      onRender: <RepliesFilter />
    },
    {
      key: 'locationFilter',
      onRender: (
        <NodeSelectorDropdown
          toggleProps={{
            type: 'static',
            toggleVariant: ButtonVariant.LINK,
            text: formatMessage('location')
          }}
          menuClassName={cx('lia-node-picker-menu')}
          selectedNode={locationFilter ? selectedNode : communityWithDepth}
          onNodeSelect={onNodeChange}
          searchPlaceholderText={formatMessage('location.search.placeholder')}
          conversationStylesToBeIncluded={
            contentTypeFilter ? [contentTypeFilter as ConversationStyle] : []
          }
        />
      )
    },
    {
      key: 'authorFilter',
      onRender: <SearchUserFilter onChange={onChange} name={formatMessage('author')} />
    },
    {
      key: 'contentTypeFilter',
      onRender: <ContentTypeFilter />
    },
    {
      key: 'labelSearchFilter',
      onRender: (
        <MessageSearchTagsFilter
          filterName={formatMessage('tags')}
          contextNode={contextNode}
          author={author}
        />
      )
    }
  ];

  overflowedItems.push({
    key: 'searchOnlyInTopicsCheckfield',
    onRender: <SearchOnlyInTopicsFilter />
  });

  return (
    <>
      <OverflowSet
        className={cx(className, 'lia-g-pt-20')}
        items={filtersItems}
        overflowSetHeading={formatMessage('allFilters')}
        moreFilterButtonText={formatMessage('moreFilters')}
        overflowedItems={overflowedItems}
      />
      <SearchAppliedFilters contextNode={contextNode} author={author} />
    </>
  );
};

export default React.memo(SearchWidgetFilters);
